<template>
  <NuxtLayout>
    <VitePwaManifest />
    <SpeedInsights />
    <NuxtPage />
    <ClientOnly>
      <Sidebar
        direction="right"
        state-key="isAuthSidebarOpen"
        :show-header="($grid.md && uiStore.state.isCartComponentVisible) || !uiStore.state.isCartComponentVisible"
      >
        <template #content>
          <Transition name="fade">
            <Login v-if="uiStore.state.isLoginComponentVisible" />
          </Transition>

          <Transition name="fade">
            <Register v-if="uiStore.state.isRegisterComponentVisible" />
          </Transition>

          <Transition name="fade">
            <RegisterConfirmation v-if="uiStore.state.isRegisterConfirmationComponentVisible" />
          </Transition>

          <Transition name="fade">
            <ForgotPassword v-if="uiStore.state.isForgotPasswordComponentVisible" />
          </Transition>

          <Transition name="fade">
            <ResetPassword v-if="uiStore.state.isResetPasswordComponentVisible" />
          </Transition>

          <Transition name="fade">
            <Logout v-if="uiStore.state.isLogoutComponentVisible" />
          </Transition>

          <Transition name="fade">
            <Microcart v-if="uiStore.state.isCartComponentVisible" :is-checkout-mode="false" transition="fade" />
          </Transition>
        </template>
      </Sidebar>
    </ClientOnly>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { useCartStore } from '@/stores/cart';
import { useUserStore } from '@/stores/user';
import { useNuxtApp } from '#imports';
import { useSeoStore } from '@/stores/seo';
import { storeToRefs } from 'pinia';
import { useUiStore } from '@/stores/ui';
import { useStoryblokStore } from '@/stores/storyblok';
import { useCategoryStore } from '@/stores/category';
import { useFeatureFlagStore } from '@/stores/featureFlag';
import { errorReload, MOTIV_SLUG_MALEN_ID, EVENTS_SLUG, VARIABLE_ROUTE_TYPE, COOKIES_PATH } from '@/resources/helpers';
import { useUiNotificationStore } from '@/stores/uiNotification';
import { SpeedInsights } from '@vercel/speed-insights/vue';

const Sidebar = defineAsyncComponent(() => import('@/components/Header/Sidebar.vue'));
const Microcart = defineAsyncComponent(() => import('@/components/Header/Microcart_v2.vue'));
const Register = defineAsyncComponent(() => import('@/components/Pages/Auth/Sidebar/register.vue'));
const RegisterConfirmation = defineAsyncComponent(
  () => import('@/components/Pages/Auth/Sidebar/register-confirmation.vue')
);
const Login = defineAsyncComponent(() => import('@/components/Pages/Auth/Sidebar/login.vue'));
const ForgotPassword = defineAsyncComponent(() => import('@/components/Pages/Auth/Sidebar/forgot-password.vue'));
const ResetPassword = defineAsyncComponent(() => import('@/components/Pages/Auth/Sidebar/reset-password.vue'));
const Logout = defineAsyncComponent(() => import('@/components/Pages/Auth/Sidebar/logout.vue'));

const CONFIRMED_EMAIL = 'confirmed-email';

const { $router, $logger, $grid, $i18n, $growthbook } = useNuxtApp();
const route = useRoute();
const config = useAppConfig();

const uiStore = useUiStore();
const storyblokStore = useStoryblokStore();
const categoryStore = useCategoryStore();

const seoStore = useSeoStore();
const { seoImageUrl } = storeToRefs(seoStore);

const featureFlagStore = useFeatureFlagStore();

const cartStore = useCartStore();

const userStore = useUserStore();

const uiNotification = useUiNotificationStore();

const page = computed(() => route.name);

async function handleRouteHash() {
  let hash = route.hash;
  const hashToComponentMap: Record<string, string> = {
    '#register': 'isRegisterComponentVisible',
    '#register-confirmation': 'isRegisterConfirmationComponentVisible',
    '#login': 'isLoginComponentVisible',
    '#forgot-password': 'isForgotPasswordComponentVisible',
    '#reset-password': 'isResetPasswordComponentVisible',
    '#logout': 'isLogoutComponentVisible'
  };

  if (hash.startsWith('#reset-password')) hash = '#reset-password';

  const componentVisibility = hashToComponentMap[hash];

  if (componentVisibility) {
    uiStore.resetState();
    uiStore.toggleState('isAuthSidebarOpen', true);
    uiStore.toggleState(componentVisibility, true);
  }

  if (hash.startsWith('#confirmation?token=')) {
    const tokenValue = hash.split('token=')[1];

    const response = await userStore.sendEmailConfirmation(tokenValue);

    if (response === 200) {
      localStorage.setItem(CONFIRMED_EMAIL, 'true');
      $router.push({ hash: '#login' });
    } else {
      uiNotification.showNotification({
        type: 'danger',
        message: $i18n.t('auth.error_sending_confirmation')
      });
    }
  }
}

onServerPrefetch(async () => {
  await Promise.all(
    [storyblokStore.fetchGlobalComponents(), categoryStore.fetchCategories()].map((p) =>
      p.catch((e) => $logger.error(e))
    )
  );
});

if (import.meta.server) {
  useSeoMeta({
    title: config.seo.headTitle,
    description: config.seo.headDescription,
    ogTitle: config.seo.headTitle,
    ogDescription: config.seo.headDescription,
    ogImage: seoImageUrl.value,
    twitterTitle: config.seo.headTitle,
    twitterDescription: config.seo.headDescription,
    twitterImage: '[twitter:image]',
    twitterCard: 'summary'
  });
}

onBeforeMount(async () => {
  window.addEventListener('vite:preloadError', (event) => {
    errorReload('Error during page load', 3);
  });

  setFeatureFlag();
  if (route.query?.cartId) {
    await cartStore.setCartId(route.query.cartId as string);
  }
  await Promise.all([cartStore.loadCart(), userStore.loadUser()].map((p) => p.catch((e) => $logger.error(e))));
});

function setFeatureFlag() {
  const {
    context: { features }
  } = $growthbook;
  // only active features are visible in features object
  for (const feature in features) {
    if ($growthbook?.isOn(feature)) {
      featureFlagStore.showFeature(feature);
    }
  }
}

watch(
  () => route,
  () => {
    setFeatureFlag();
  }
);

watch(
  () => route.hash,
  () => {
    handleRouteHash();
  }
);

useHead({
  htmlAttrs: {
    lang: 'de'
  },
  link: [
    {
      rel: 'icon',
      type: 'image/png',
      href: '/favicon--AN.png'
    }
  ]
});

function hideWidget(widget) {
  if (!widget) return;
  widget.style.display = 'none';
  widget.style.opacity = '0';
  widget.style.pointerEvents = 'none';
}

function showWidget(widget) {
  if (!widget) return;
  widget.style.display = ''; // restore display
  widget.style.opacity = '1';
  widget.style.pointerEvents = 'auto';
}

watch(
  () => route.path,
  (newVal) => {
    toggleWidget();
  }
);

function toggleWidget() {
  const widgetWrapper = document.querySelector('cookie-widget-wrapper');
  if (!widgetWrapper) return false;

  // Access the shadow DOM (if available)
  const shadowRoot = widgetWrapper.shadowRoot;
  if (!shadowRoot) return false;

  const widget = shadowRoot.querySelector('.gt-cookie-reopen-btn');
  if (!widget) return;

  if (window.location.pathname === '/cookies') {
    showWidget(widget);
  } else {
    hideWidget(widget);
  }
}

let observer;
let checkInterval;

onMounted(() => {
  handleRouteHash();
  seoStore.getSeoImage();

  // Initial toggle
  toggleWidget();

  // Create observer to watch for the widget being added to DOM
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length) {
        for (const node of mutation.addedNodes) {
          if (node.nodeName && node.nodeName.toLowerCase() === 'cookie-widget-wrapper') {
            setTimeout(() => toggleWidget(), 100);
            return;
          }
        }
      }
    });
  });

  // Start observing
  observer.observe(document.body, { childList: true, subtree: true });

  // Set up interval as fallback
  checkInterval = setInterval(() => {
    toggleWidget();
  }, 500);

  // Stop checking after 10 seconds
  setTimeout(() => {
    clearInterval(checkInterval);
  }, 10000);
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
  if (checkInterval) {
    clearInterval(checkInterval);
  }
});
</script>
<style lang="scss">
.cookie-widget-hidden {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.cookie-widget-visible {
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.3s ease;
}
</style>
